import { criterion, fail, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  isPlaceholder,
  longestParagraphWords,
  looksLikeSentence,
  readPresentation,
  slideWordCount,
} from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

export const lektionTextAbschluss: Lesson = {
  id: "abschlussuebung",
  title: "Abschlussübung: eine Folie, die trägt",
  summary: "Textmenge, Stichpunktlänge und Schriftgröße an einer Folie zusammengeführt.",
  durationMinutes: 9,
  learningGoals: [
    "Eine Inhaltsfolie nach allen Regeln des Moduls aufbauen",
    "Die Textmenge einer ganzen Folie einschätzen",
  ],
  steps: [
    {
      id: "wortzahl",
      title: "Die Wortzahl als grober Maßstab",
      explanation: [
        "Ein einfacher Maßstab für „zu viel Text“ ist die Wortzahl der gesamten Folie, " +
          "Titel eingerechnet. Bis etwa 40 Wörter bleibt eine Folie überschaubar. " +
          "Zwischen 40 und 60 wird es eng. Darüber liest das Publikum, statt zuzuhören.",
        "Die Zahl ist kein Naturgesetz. Eine Folie mit einer Tabelle hat naturgemäß mehr " +
          "Wörter, eine Zitatfolie deutlich weniger. Als Warnschwelle beim Durchsehen " +
          "einer fremden Präsentation funktioniert sie aber gut.",
        "Falls dir eine Folie beim Kürzen partout nicht kleiner werden will, ist das " +
          "meistens ein Zeichen dafür, dass sie zwei Aussagen auf einmal trifft. Dann ist " +
          "Teilen nicht nur eine Formfrage, sondern verbessert auch den Vortrag: Jede " +
          "Folie trägt genau eine Aussage, und der Titel sagt, welche.",
      ],
      keyPoints: [
        "Richtwert: bis 40 Wörter je Folie inklusive Titel.",
        "Eine Folie, eine Aussage – der Titel benennt sie.",
        "Lässt sich eine Folie nicht kürzen, enthält sie zwei Themen.",
      ],
    },
    {
      id: "abschluss",
      title: "Abschlussübung",
      explanation: [
        "Bau eine Inhaltsfolie, die alle Regeln dieses Moduls erfüllt: höchstens sechs " +
          "Stichpunkte, keiner länger als zehn Wörter oder ein ganzer Satz, insgesamt " +
          "höchstens 40 Wörter, Schrift nicht unter 18 pt.",
      ],
      keyPoints: [
        "Erst schreiben, dann kürzen – in dieser Reihenfolge geht es schneller.",
        "Was gestrichen wird, ist nicht weg: Es gehört in die Sprechernotizen.",
        "Zu viel Inhalt heißt teilen, nicht verkleinern.",
      ],
      task: {
        instruction:
          "Mindestens eine Folie deiner Präsentation soll alle vier Punkte erfüllen: " +
          "höchstens sechs Stichpunkte, jeder unter elf Wörtern und ohne Satzpunkt, " +
          "insgesamt höchstens 40 Wörter, keine Schrift unter 18 pt.",
        hint: "Nimm dir eine bestehende Folie vor und kürze sie, statt eine neue anzulegen.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.text) {
            return nichtPruefbar(
              "Der Text in den Platzhaltern",
              "1.4",
              "Geh die vier Punkte an einer Folie selbst durch.",
            );
          }

          const kandidaten = snapshot.slides.filter((folie) =>
            folie.shapes.some(
              (form) =>
                isPlaceholder(form) &&
                form.paragraphs.length > 0 &&
                form.placeholder?.type !== "Title" &&
                form.placeholder?.type !== "CenterTitle" &&
                !/^(title|titel)\b/i.test(form.name ?? ""),
            ),
          );

          if (kandidaten.length === 0) {
            return fail(
              "Es gibt noch keine Folie mit gefülltem Inhaltsplatzhalter.",
              "Lege eine Folie mit dem Layout „Titel und Inhalt“ an und schreibe Stichpunkte hinein.",
            );
          }

          const bewerte = (folie: (typeof kandidaten)[number]) => {
            const texte = folie.shapes.filter(
              (form) => isPlaceholder(form) && form.paragraphs.length > 0,
            );
            const stichpunkte = Math.max(
              ...texte
                .filter(
                  (form) =>
                    form.placeholder?.type !== "Title" &&
                    form.placeholder?.type !== "CenterTitle" &&
                    !/^(title|titel)\b/i.test(form.name ?? ""),
                )
                .map((form) => form.paragraphs.length),
              0,
            );
            const laengster = Math.max(...texte.map(longestParagraphWords), 0);
            const saetze = texte.some((form) => form.paragraphs.some(looksLikeSentence));
            const groessen = texte
              .map((form) => form.font?.size)
              .filter((groesse): groesse is number => typeof groesse === "number");
            const kleinste = groessen.length > 0 ? Math.min(...groessen) : null;
            const woerter = slideWordCount(folie);

            return {
              folie,
              stichpunkte,
              laengster,
              saetze,
              kleinste,
              woerter,
              erfuellt:
                stichpunkte <= 6 &&
                stichpunkte > 0 &&
                laengster <= 10 &&
                !saetze &&
                woerter <= 40 &&
                (kleinste === null || kleinste >= 18),
            };
          };

          const bewertet = kandidaten.map(bewerte);
          const beste = bewertet.find((eintrag) => eintrag.erfuellt) ?? bewertet[0];

          return fromCriteria(
            [
              criterion(
                `Höchstens sechs Stichpunkte (Folie ${beste.folie.index + 1}: ${beste.stichpunkte})`,
                beste.stichpunkte > 0 && beste.stichpunkte <= 6,
              ),
              criterion(
                `Kein Stichpunkt länger als zehn Wörter (längster: ${beste.laengster})`,
                beste.laengster <= 10,
              ),
              criterion("Kein ausformulierter Satz mit Punkt am Ende", !beste.saetze),
              criterion(
                `Höchstens 40 Wörter auf der Folie (gezählt: ${beste.woerter})`,
                beste.woerter <= 40,
              ),
              criterion(
                beste.kleinste === null
                  ? "Die Schriftgröße ist gemischt und deshalb nicht ablesbar"
                  : `Schrift nicht unter 18 pt (kleinste: ${beste.kleinste} pt)`,
                beste.kleinste === null || beste.kleinste >= 18,
              ),
            ],
            {
              success: `Folie ${beste.folie.index + 1} erfüllt alle Punkte – sie trägt eine Aussage, ohne den Vortrag zu ersetzen.`,
              failure: `Am nächsten dran ist bisher Folie ${beste.folie.index + 1} mit ${beste.woerter} Wörtern insgesamt.`,
              hint: "Was du streichst, ist nicht verloren – schreib es in die Sprechernotizen. Wie das geht, steht im Modul „Sprechernotizen und Präsentieren“.",
            },
          );
        },
      },
    },
  ],
};
